// Visualization3D CP-1B Page - Using 3D Blender Model
import React, { useState, useEffect, useCallback } from 'react';
import { useAuthContext as useAuth } from '../contexts/AuthContext';
import { permissionChecks } from '../lib/auth/helpers';
import { Fresh3DVisualizationEngine } from '../components/visualization/Fresh3DVisualizationEngine';
import { thresholdService, ThresholdServiceStatic } from '../services/thresholdService';
import { OccupancyServiceStatic } from '../services/occupancyService';
import { logger } from '../utils/logging/logger';

export const Visualization3DCP1BPage: React.FC = () => {
  const { user } = useAuth();
  const [currentOccupancyData, setCurrentOccupancyData] = useState<Record<string, number> | null>(null);
  const [currentFlowData, setCurrentFlowData] = useState<Record<string, number> | null>(null);
  const [apiThresholds, setApiThresholds] = useState<Record<string, { green: number; yellow: number; amber: number }> | null>(null);
  const [isLoadingThresholds, setIsLoadingThresholds] = useState(false);
  const [thresholdLoadError, setThresholdLoadError] = useState<string | null>(null);
  const [thresholdSource, setThresholdSource] = useState<'api' | 'cache' | 'default'>('default');
  const [occupancyError, setOccupancyError] = useState<string | null>(null);
  const [isLoadingOccupancy, setIsLoadingOccupancy] = useState(true);

  // Check 3D view permission
  const hasAccess = permissionChecks.can3dVisualisationAccess();

  // Subscribe to occupancy data updates (same as 2D)
  useEffect(() => {
    // Start periodic fetch
    OccupancyServiceStatic.startPeriodicFetch();
    
    // Subscribe to data updates
    const unsubscribe = OccupancyServiceStatic.subscribe((data, error) => {
      if (data) {
        setCurrentOccupancyData(data.occupancy);
        setCurrentFlowData(data.flowOut);
        setIsLoadingOccupancy(false);
        setOccupancyError(null);
      } else if (error) {
        setOccupancyError(error);
        if (!OccupancyServiceStatic.hasData()) {
          setIsLoadingOccupancy(false);
        }
      }
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  // Updated threshold fetching with automatic refresh (same as 2D)
  const fetchThresholds = async (isAutoRefresh = false) => {
    if (!isAutoRefresh) {
      setIsLoadingThresholds(true);
    }
    setThresholdLoadError(null);
    
    try {
      const thresholds = await thresholdService.getThresholds();
      
      // Filter for CP-1B thresholds only
      const cp1bThresholds = Object.fromEntries(
        Object.entries(thresholds).filter(([assetCode]) => assetCode.startsWith('t1-1b_'))
      );
      
      setApiThresholds(cp1bThresholds);
      
      // Determine source for status display
      const cached = ThresholdServiceStatic.getCachedThresholds();
      if (cached && Object.keys(cached).length > 0) {
        setThresholdSource('cache');
      } else if (Object.keys(cp1bThresholds).length > 0) {
        setThresholdSource('api');
      } else {
        setThresholdSource('default');
      }
      
      logger.visualization3D('3D CP-1B thresholds loaded:', {
        count: Object.keys(cp1bThresholds).length,
        source: cached ? 'cache' : 'api',
        isAutoRefresh
      });
      
    } catch (error) {
      console.error('Failed to load thresholds:', error);
      setThresholdLoadError('Failed to load threshold configuration');
      setThresholdSource('default');
      setApiThresholds(null);
    } finally {
      if (!isAutoRefresh) {
        setIsLoadingThresholds(false);
      }
    }
  };

  // Auto-refresh callback for periodic updates
  const handleAutoRefresh = useCallback((thresholds: any) => {
    fetchThresholds(true); // Silent refresh
  }, []);

  // Load thresholds on mount and start periodic refresh
  useEffect(() => {
    fetchThresholds();
    
    // Start automatic refresh with callback
    ThresholdServiceStatic.startPeriodicRefresh(handleAutoRefresh);
    
    // Cleanup on unmount
    return () => {
      ThresholdServiceStatic.stopPeriodicRefresh();
    };
  }, [handleAutoRefresh]);

  // If no access, show permission denied
  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">You don't have permission to access 3D View features.</p>
          <p className="text-gray-500 text-sm mt-2">Required permission: 3d_visualisation_access</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-900 relative">
      {/* Local loading indicator for thresholds */}
      {isLoadingThresholds && (
        <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-2 rounded-md text-sm z-10">
          Loading thresholds...
        </div>
      )}
      
      {/* Loading indicator for occupancy data */}
      {isLoadingOccupancy && (
        <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-2 rounded-md text-sm z-10">
          Loading occupancy data...
        </div>
      )}
      
      {/* Threshold error indicator */}
      {thresholdLoadError && (
        <div className="absolute top-4 left-4 bg-yellow-600 text-white px-3 py-2 rounded-md text-sm z-10">
          {thresholdLoadError} - Using defaults
        </div>
      )}
      
      {/* Occupancy error indicator */}
      {occupancyError && (
        <div className="absolute top-16 right-4 bg-orange-600 text-white px-3 py-2 rounded-md text-sm z-10">
          Data connection issues
        </div>
      )}

      <Fresh3DVisualizationEngine
        modelPath="/models/cp-1b_v2_complete.glb"
        occupancyData={currentOccupancyData}
        flowData={currentFlowData}
        thresholds={apiThresholds ? 
          Object.values(apiThresholds)[0] || { green: 20, yellow: 25, amber: 30 }
          : { green: 20, yellow: 25, amber: 30 }
        }
        checkpointId="CP-1B"
        className="w-full h-full"
      />
    </div>
  );
};
